/**
 * API client. All requests go to the same origin (`/api/*`) so the identity
 * cookie set at empathycorp.us is included automatically. For local dev
 * Vite proxies /api → :3623; cross-origin would lose the cookie.
 */

export type WhoAmI =
  | { authenticated: false }
  | { authenticated: true; user_id: string; email: string; method: string }

export async function api<T = any>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
    ...init,
  })
  if (res.status === 401) {
    throw new ApiError('Not authenticated', 401)
  }
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`
    try {
      const j = await res.json()
      if (j?.detail) detail = j.detail
    } catch {}
    throw new ApiError(detail, res.status)
  }
  if (res.status === 204) return undefined as any
  return (await res.json()) as T
}

export class ApiError extends Error {
  status: number
  constructor(msg: string, status: number) {
    super(msg)
    this.status = status
  }
}

export async function whoami(): Promise<WhoAmI> {
  try {
    return await api<WhoAmI>('/api/auth/whoami')
  } catch (e: any) {
    if (e?.status === 401) return { authenticated: false }
    throw e
  }
}

// Routes
export const me = () => api<MeResponse>('/api/me')
export const listCards = () => api<{ cards: Card[] }>('/api/cards')
export const createCard = (c: CardInput) =>
  api<{ card: Card }>('/api/cards', { method: 'POST', body: JSON.stringify(c) })
export const closeCard = (id: number) =>
  api<{ ok: boolean }>(`/api/cards/${id}`, { method: 'DELETE' })
export const recommend = (amount: number, category: string) =>
  api<RecommendResponse>('/api/recommend', {
    method: 'POST',
    body: JSON.stringify({ amount, category }),
  })
export const listConnections = () => api<ConnectionsResponse>('/api/connections')
export const listRates = () => api<{ rates: Rate[] }>('/api/rates')
export const balances = () => api<{ accounts: AccountBalance[] }>('/api/plaid/balances')
export const plaidLinkToken = () =>
  api<{ link_token: string; expiration: string }>('/api/plaid/link-token', {
    method: 'POST',
    body: JSON.stringify({ products: ['transactions'] }),
  })
export const plaidExchange = (
  public_token: string,
  institution_name?: string,
  institution_id?: string,
) =>
  api<{ ok: boolean; plaid_item_id: number; item_id: string }>('/api/plaid/exchange', {
    method: 'POST',
    body: JSON.stringify({ public_token, institution_name, institution_id }),
  })
export const plaidSync = () => api<{ ok: boolean; items_synced: number }>('/api/plaid/sync', { method: 'POST' })
export const gmailAuthUrl = (label: string) =>
  api<{ authorization_url: string }>(`/api/gmail/auth-url?label=${encodeURIComponent(label)}`)
export const listPrograms = () => api<{ programs: Program[] }>('/api/programs')
export const upsertProgram = (program: string, body: { balance: number; transfer_value_cpp: number | null }) =>
  api<{ program: string; balance: number; transfer_value_cpp: number | null; updated_at: string }>(
    `/api/programs/${encodeURIComponent(program)}`,
    { method: 'PUT', body: JSON.stringify(body) },
  )
export const deleteProgramOverride = (program: string) =>
  api<{ ok: boolean }>(`/api/programs/${encodeURIComponent(program)}`, { method: 'DELETE' })

// Types
export interface MeResponse {
  user_id: number | null
  email: string | null
  tenant_id: number | null
  method: string | null
  summary: {
    card_count: number
    linked_accounts: number
    open_subs: number
    five_24_count: number
  }
}

export interface Card {
  id: number
  card_key: string
  name: string
  issuer: string | null
  network: string | null
  is_business: boolean
  status: string
  opened: string | null
  credit_limit: number | null
  apr_purchase: number | null
  apr_zero_pct_until: string | null
  statement_day: number | null
  annual_fee: number | null
  af_due: string | null
  points_program: string | null
  velocity_family: string | null
  notes: string | null
  reward_categories: Array<{
    cat: string
    multiplier: number
    cap_per_year?: number | null
    portal_only?: boolean
  }>
  sub: null | {
    required: number
    earned: number
    by_date: string
    cleared?: boolean
    bonus_value_dollars?: number
  }
}

export type CardInput = Omit<Card, 'id'>

export interface RecommendResponse {
  category: string
  amount: number
  ranked: Array<{
    card_id: number
    card_key: string
    name: string
    reward_dollars: number
    sub_dollars: number
    total_value: number
    multiplier: number
    cpp: number
    portal_only: boolean
    reasoning: string
  }>
}

export interface AccountBalance {
  id: number
  name: string
  official_name: string | null
  type: string | null
  subtype: string | null
  mask: string | null
  currency: string
  available: number | null
  current: number | null
  as_of: string | null
  institution_name: string | null
}

export interface Rate {
  source: string
  instrument: string
  rate: number
  as_of_date: string
  fetched_at: string
}

export interface Program {
  program: string
  balance: number
  override_cpp: number | null
  fallback_cpp: number | null
  effective_cpp: number
  source: 'override' | 'fallback' | 'none'
  updated_at: string | null
  in_use_on_cards: boolean
}

export interface ConnectionsResponse {
  plaid_items: Array<{
    id: number
    item_id: string
    institution_name: string | null
    status: string
    created_at: string
    last_sync_at: string | null
  }>
  connections: Array<{
    id: number
    provider: string
    label: string | null
    status: string
    created_at: string
    updated_at: string
    metadata: any
  }>
}
